import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Icon from './Icon';
import WebSocketStatus from './WebSocketStatus';
import TrialCounter from './TrialCounter';
import ThemeToggle from './ThemeToggle';
import axios from 'axios';
import styles from './Sidebar.module.css';

export default function Sidebar({ isMobile, isOpen, onClose, onOpenTour }) {
  const { user, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [lockedFeature, setLockedFeature] = useState('');
  const [isOnboardingPending, setIsOnboardingPending] = useState(false);

  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002'}/api/doctor/dashboard`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success && response.data.dashboard) {
          const { incompleteConfig } = response.data.dashboard;
          setIsOnboardingPending(incompleteConfig && incompleteConfig.length > 0);
        }
      } catch (err) {
        console.error('Error al verificar onboarding en Sidebar:', err);
      }
    };

    if (user) {
      checkOnboarding();
    }
  }, [user]);

  const menuItems = [
    { icon: 'home', label: 'Dashboard', path: '/dashboard' },
    ...(isOnboardingPending ? [{ icon: 'reports', label: 'Guía de Inicio', path: '/onboarding' }] : []),
    { icon: 'calendar', label: 'Gestión de Turnos', path: '/appointments' },
    { icon: 'clock', label: 'Fila en Vivo (Orden)', path: '/queue' },
    { icon: 'users', label: 'Clientes', path: '/patients' },
    { icon: 'briefcase', label: 'Mis Servicios', path: '/services' },
    { icon: 'reports', label: 'Estadísticas', path: '/reports' },
    { icon: 'wallet', label: 'Caja y Movimientos', path: '/movements' },
    { icon: 'shield', label: 'Convenios', path: '/insurance' },
    { icon: 'clock', label: 'Horarios de Trabajo', path: '/working-hours' },
    { icon: 'bell', label: 'Notificaciones', path: '/notifications' },
    { icon: 'warning', label: 'Reportar Problema', path: '/support' },
    { icon: 'settings', label: 'Configuración', path: '/settings' },
  ];

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      <aside className={`
        ${styles.sidebar} 
        ${isCollapsed ? styles.collapsed : ''} 
        ${isOpen ? styles.open : ''}
      `}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.logo}>
            <span className={`material-symbols-outlined ${styles.logoIcon}`}>hub</span>
            {(!isCollapsed || isOpen) && <span className={styles.logoText}>TurnoHub</span>}
          </div>
          
          {/* Botón colapsar (solo escritorio) */}
          {!isOpen && (
            <button
              className={styles.collapseBtn}
              onClick={() => setIsCollapsed(!isCollapsed)}
              title={isCollapsed ? 'Expandir' : 'Contraer'}
            >
              {isCollapsed ? '›' : '‹'}
            </button>
          )}

          {/* Botón cerrar (solo móvil) */}
          {isOpen && (
            <button className={styles.closeBtn} onClick={onClose} aria-label="Cerrar menú">
              <Icon name="x" size={24} color="currentColor" />
            </button>
          )}
        </div>

        {/* Menu items */}
        <nav className={styles.nav}>
          {menuItems.map(item => {
            const itemLockedFeature = item.path === '/insurance' && user?.plan?.allow_insurance === false
              ? 'Convenios y Obras Sociales'
              : null;
            const isLocked = Boolean(itemLockedFeature);
            return (
              <NavLink
                key={item.path}
                to={isLocked ? '#' : item.path}
                className={({ isActive }) => 
                  `${styles.navItem} ${isActive && !isLocked ? styles.active : ''} ${isLocked ? styles.lockedItem : ''}`
                }
                title={isCollapsed ? item.label : ''}
                onClick={(e) => {
                  if (isLocked) {
                    e.preventDefault();
                    setLockedFeature(itemLockedFeature);
                    setShowUpgradeModal(true);
                  } else if (isOpen) {
                    onClose();
                  }
                }}
              >
                <Icon name={item.icon} size={20} color="currentColor" />
                {(!isCollapsed || isOpen) && <span className={styles.label}>{item.label}</span>}
                {isLocked && (!isCollapsed || isOpen) && <span className={styles.lockBadge}>🔒</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* Mobile Tutorial Button */}
        {isOpen && onOpenTour && (
          <div className={styles.mobileTourContainer}>
            <button 
              onClick={() => {
                onOpenTour();
                onClose();
              }}
              className={styles.mobileTourBtn}
            >
              <span className="material-symbols-outlined">explore</span>
              <span>Tutorial de TurnoHub</span>
            </button>
          </div>
        )}

        {/* Mobile Status Section */}
        <div className={styles.sidebarStatus}>
          <ThemeToggle showLabel={!isCollapsed || isOpen} />
          <WebSocketStatus />
          <TrialCounter />
        </div>

        {/* Footer con usuario */}
        <div className={styles.footer}>
          {(!isCollapsed || isOpen) && user && (
            <div className={styles.userInfo}>
              <div className={styles.userName}>{user.name || 'Profesional'}</div>
              <div className={styles.userEmail}>{user.email}</div>
            </div>
          )}
          <button
            className={styles.logoutBtn}
            onClick={handleLogout}
            title="Cerrar sesión"
          >
            <Icon name="logout" size={20} color="currentColor" />
            {(!isCollapsed || isOpen) && <span>Cerrar sesión</span>}
          </button>
        </div>
      </aside>

      {/* Upgrade Modal overlay */}
      {showUpgradeModal && (
        <div className={styles.modalOverlay} onClick={() => setShowUpgradeModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <span className={`material-symbols-outlined ${styles.modalLockIcon}`}>lock</span>
              <h3>Funcionalidad Exclusiva</h3>
            </div>
            <div className={styles.modalBody}>
              <p>El acceso al módulo de <strong>{lockedFeature || 'esta funcionalidad'}</strong> no está habilitado en tu plan actual (<strong>{user?.plan?.name || 'Plan Básico'}</strong>).</p>
              <p className={styles.modalInstruction}>Contacta al administrador del sistema para solicitar un ascenso de plan.</p>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.upgradeBtn} onClick={() => setShowUpgradeModal(false)}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
